import React, { useEffect } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker, DatePicker } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider } from "@mui/material";
import { FormHelperText } from "@mui/material";
// import { frFR, enUS } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "dayjs/locale/fr"; // Import the French locale
import { useIntl } from "react-intl";
import { inputStyles } from "./styles/inputStyles";

// Set the locale globally
dayjs.locale("fr");

const Datepicker = ({
    eventData,
    handleChange,
    dateError,
    setDateError,
    locale,
}) => {
    const intl = useIntl();
    const theme = createTheme(
        {
            palette: {
                mode: "light",
            },
        }
        // locale === "fr" ? frFR : enUS
    );
    useEffect(() => {
        const start = dayjs(eventData.start_date);
        const end = dayjs(eventData.end_date);
        if (end.isBefore(start)) {
            setDateError(true);
        } else {
            setDateError(false);
        }
    }, [eventData]);

    // Helper function to calculate the duration between start and end dates
    const calculateDuration = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        return end.diff(start, "minute");
    };

    // Helper function to add duration to a date
    const addDurationToDate = (date, durationInMinutes) => {
        return dayjs(date)
            .add(durationInMinutes, "minute")
            .format("YYYY-MM-DDTHH:mm");
    };

    const rowStyle =
        "flex items-center justify-center md:justify-between  gap-1 md:gap-2 ";

    return (
        <>
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={locale}
            >
                <ThemeProvider theme={theme}>
                    <div className="flex flex-col gap-4 w-full">
                        <div className={rowStyle}>
                            <div className="w-1/3 md:w-1/2">
                                <DatePicker
                                    label="Date de début"
                                    value={
                                        eventData.start_date
                                            ? dayjs(eventData.start_date)
                                            : null
                                    }
                                    onChange={(date) => {
                                        if (date) {
                                            // Preserve the time from the existing date or set to 00:00 if none exists
                                            const currentTime =
                                                eventData.start_date
                                                    ? dayjs(
                                                          eventData.start_date
                                                      ).format("HH:mm")
                                                    : "00:00";
                                            // Combine the new date with the existing time
                                            const formattedDate =
                                                dayjs(date).format(
                                                    "YYYY-MM-DD"
                                                ) +
                                                "T" +
                                                currentTime;

                                            // Calculate the current duration between start and end
                                            const durationInMinutes =
                                                calculateDuration(
                                                    eventData.start_date,
                                                    eventData.end_date
                                                );

                                            // Update start date
                                            handleChange({
                                                target: {
                                                    name: "start_date",
                                                    value: formattedDate,
                                                },
                                            });

                                            // If end date exists, adjust it to maintain the same duration
                                            if (
                                                eventData.end_date &&
                                                durationInMinutes > 0
                                            ) {
                                                const newEndDate =
                                                    addDurationToDate(
                                                        formattedDate,
                                                        durationInMinutes
                                                    );

                                                handleChange({
                                                    target: {
                                                        name: "end_date",
                                                        value: newEndDate,
                                                    },
                                                });
                                            }
                                        } else {
                                            // Handle null case
                                            handleChange({
                                                target: {
                                                    name: "start_date",
                                                    value: null,
                                                },
                                            });
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    slotProps={{
                                        textField: {
                                            sx: {
                                                ...inputStyles,
                                                width: "100%",
                                            },
                                            fullWidth: true,
                                            variant: "filled",
                                        },
                                    }}
                                />
                            </div>
                            <div className="w-1/3 md:w-1/2">
                                <TimePicker
                                    label="Heure de début"
                                    value={
                                        eventData.start_date
                                            ? dayjs(eventData.start_date)
                                            : null
                                    }
                                    onChange={(time) => {
                                        if (time) {
                                            // Preserve the date from the existing datetime or use today if none exists
                                            const currentDate =
                                                eventData.start_date
                                                    ? dayjs(
                                                          eventData.start_date
                                                      ).format("YYYY-MM-DD")
                                                    : dayjs().format(
                                                          "YYYY-MM-DD"
                                                      );

                                            // Calculate the current duration between start and end
                                            const durationInMinutes =
                                                calculateDuration(
                                                    eventData.start_date,
                                                    eventData.end_date
                                                );

                                            // Combine the existing date with the new time
                                            const formattedDateTime =
                                                currentDate +
                                                "T" +
                                                dayjs(time).format("HH:mm");

                                            // Update start date
                                            handleChange({
                                                target: {
                                                    name: "start_date",
                                                    value: formattedDateTime,
                                                },
                                            });

                                            // If end date exists, adjust it to maintain the same duration
                                            if (
                                                eventData.end_date &&
                                                durationInMinutes > 0
                                            ) {
                                                const newEndDate =
                                                    addDurationToDate(
                                                        formattedDateTime,
                                                        durationInMinutes
                                                    );

                                                handleChange({
                                                    target: {
                                                        name: "end_date",
                                                        value: newEndDate,
                                                    },
                                                });
                                            }
                                        } else {
                                            // If time is cleared but we have a date, keep just the date part
                                            if (eventData.start_date) {
                                                const dateOnly =
                                                    dayjs(
                                                        eventData.start_date
                                                    ).format("YYYY-MM-DD") +
                                                    "T00:00";
                                                handleChange({
                                                    target: {
                                                        name: "start_date",
                                                        value: dateOnly,
                                                    },
                                                });
                                            } else {
                                                handleChange({
                                                    target: {
                                                        name: "start_date",
                                                        value: null,
                                                    },
                                                });
                                            }
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    slotProps={{
                                        textField: {
                                            sx: {
                                                ...inputStyles,
                                                width: "100%",
                                            },
                                            fullWidth: true,
                                            variant: "filled",
                                        },
                                    }}
                                />
                            </div>
                        </div>
                        <div className={rowStyle}>
                            <div className="w-1/3 md:w-1/2">
                                <DatePicker
                                    label="Date de fin"
                                    value={
                                        eventData.end_date
                                            ? dayjs(eventData.end_date)
                                            : null
                                    }
                                    onChange={(date) => {
                                        if (date) {
                                            // Preserve the time from the existing date or set to 00:00 if none exists
                                            const currentTime =
                                                eventData.end_date
                                                    ? dayjs(
                                                          eventData.end_date
                                                      ).format("HH:mm")
                                                    : "00:00";
                                            // Combine the new date with the existing time
                                            const formattedDate =
                                                dayjs(date).format(
                                                    "YYYY-MM-DD"
                                                ) +
                                                "T" +
                                                currentTime;
                                            handleChange({
                                                target: {
                                                    name: "end_date",
                                                    value: formattedDate,
                                                },
                                            });
                                        } else {
                                            // Handle null case
                                            handleChange({
                                                target: {
                                                    name: "end_date",
                                                    value: null,
                                                },
                                            });
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    slotProps={{
                                        textField: {
                                            sx: {
                                                ...inputStyles,
                                                width: "100%",
                                            },
                                            fullWidth: true,
                                            variant: "filled",
                                        },
                                    }}
                                />
                            </div>
                            <div className="w-1/3 md:w-1/2">
                                <TimePicker
                                    label="Heure de fin"
                                    value={
                                        eventData.end_date
                                            ? dayjs(eventData.end_date)
                                            : null
                                    }
                                    onChange={(time) => {
                                        if (time) {
                                            // Preserve the date from the existing datetime or use today if none exists
                                            const currentDate =
                                                eventData.end_date
                                                    ? dayjs(
                                                          eventData.end_date
                                                      ).format("YYYY-MM-DD")
                                                    : dayjs().format(
                                                          "YYYY-MM-DD"
                                                      );
                                            // Combine the existing date with the new time
                                            const formattedDateTime =
                                                currentDate +
                                                "T" +
                                                dayjs(time).format("HH:mm");
                                            handleChange({
                                                target: {
                                                    name: "end_date",
                                                    value: formattedDateTime,
                                                },
                                            });
                                        } else {
                                            // If time is cleared but we have a date, keep just the date part
                                            if (eventData.end_date) {
                                                const dateOnly =
                                                    dayjs(
                                                        eventData.end_date
                                                    ).format("YYYY-MM-DD") +
                                                    "T00:00";
                                                handleChange({
                                                    target: {
                                                        name: "end_date",
                                                        value: dateOnly,
                                                    },
                                                });
                                            } else {
                                                handleChange({
                                                    target: {
                                                        name: "end_date",
                                                        value: null,
                                                    },
                                                });
                                            }
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    slotProps={{
                                        textField: {
                                            sx: {
                                                ...inputStyles,
                                                width: "100%",
                                            },
                                            fullWidth: true,
                                            variant: "filled",
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {dateError && (
                        <div className="">
                            <FormHelperText error={dateError}>
                                {intl.formatMessage({
                                    id: "meetingModal.dateError",
                                })}
                            </FormHelperText>
                        </div>
                    )}
                </ThemeProvider>
            </LocalizationProvider>
        </>
    );
};

export default Datepicker;
